import { Download, FileText, Sparkles, ArrowRight, Calendar, Check, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";
import { apiGet } from "@/lib/api";

// Einsteigerprogramm – Angebote dynamisch aus dem Backend, automatisch anhand des Datums verwaltet

const HIDE_AFTER_DAYS = 14; // Nach 14 Tagen Ablauf komplett verstecken

const parseLocalDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const mapEinsteiger = (e) => ({
  id: e.id,
  title: e.title,
  month: e.month,
  validFromIso: e.valid_from,
  validUntilIso: e.valid_until,
  pdfUrl: e.pdf_url,
  description: e.description,
});

// Status-Logik: new = aktuell gültig, expired = abgelaufen, hidden = zu alt
const statusOf = (offer, today) => {
  const from = parseLocalDate(offer.validFromIso);
  const until = parseLocalDate(offer.validUntilIso);
  if (today < from) return "upcoming";
  if (today <= until) return "current";
  const daysSinceExpiry = Math.round((today - until) / (1000 * 60 * 60 * 24));
  if (daysSinceExpiry > HIDE_AFTER_DAYS) return "hidden";
  return "expired";
};

const daysUntil = (iso) => {
  const d = parseLocalDate(iso);
  return Math.round((d - startOfToday()) / (1000 * 60 * 60 * 24));
};

const CAMPAIGN_IMAGE = "https://assets.foleon.com/eu-central-1/de-uploads-7e3kk3/51743/01692_tm_rekrutierungskampagne_april_2026_9zu16_01_final.d0f8ce5a451d.jpg?jwt=eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJmIjoiZDBmOGNlNWE0NTFkLmpwZyIsImV4cCI6MzMzMTI3MTM5NzAsImlhdCI6MTc3NjcxMzk3MH0.RJI8YvTQhq8hWaDt-AfnfFoTwCu0UuOFX5O8AGOEQtG52C5O6oyrGhLqv4Uv0HX-OjVFn5a5FLpwKABlJI2l2rTdsQ2Nx8LDR1jdpXuu0y7X3hw6Ij5Q7021cQPbSNGTcvIwkCZfdVPowt_ZE9yrW2KXMsD1DDo-SHGLcf6GcVE-p5BGetayqyvbEe9NHpa5i6mVedkZ6B_rJJr2IWLGEi6rCd1FW9P9XoMO1mhcs_8L1QHsk8bmxKrNOytV2EVu30Rgtde-JzMz_jUTNzNh3FFvxiZDbobdFD__pmdpOr4-23UJc-8t_C4sc_9djijIzRGFr5Xwt2tD3rvzSiQkkQ";

const EinsteigerSection = () => {
  const today = startOfToday();
  const [offers, setOffers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useScrollReveal(offers.length);

  useEffect(() => {
    apiGet("/content/einsteiger")
      .then((data) => setOffers(data.map(mapEinsteiger)))
      .catch(() => setOffers([]))
      .finally(() => setLoaded(true));
  }, []);

  // Angebote mit Status versehen und sortieren: current > upcoming > expired (hidden raus)
  const visibleOffers = offers
    .map((o) => ({ ...o, status: statusOf(o, today) }))
    .filter((o) => o.status !== "hidden")
    .sort((a, b) => {
      const order = { current: 0, upcoming: 1, expired: 2 };
      return order[a.status] - order[b.status];
    });

  // Wenn gar kein Angebot mehr da, Section komplett verstecken
  if (!loaded) return null;
  if (visibleOffers.length === 0) {
    return null;
  }

  return (
    <section
      id="einsteiger"
      data-testid="einsteiger-section"
      className="section-padding bg-stone-50 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 bg-amber-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Aktuelles Angebot
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 font-[Outfit]">
            Thermomix® Einsteiger-Programm Krefeld
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Starte als Thermomix® Repräsentant*in – ohne Startkapital.
            Lade dir die aktuellen Info-Flyer direkt herunter.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          {/* Linke Spalte: Kampagnen-Bild */}
          <div className="lg:col-span-2 reveal">
            <div className="glass-card rounded-[2rem] p-3 overflow-hidden">
              <img
                src={CAMPAIGN_IMAGE}
                alt="Thermomix Rekrutierungs-Kampagne"
                data-testid="einsteiger-campaign-image"
                loading="lazy"
                className="w-full h-auto rounded-[1.5rem] aspect-[9/16] object-cover"
              />
            </div>
            <p className="text-center text-xs text-slate-500 mt-4 font-medium">
              Aktuelle Kampagne
            </p>
          </div>

          {/* Rechte Spalte: Download-Karten */}
          <div className="lg:col-span-3 space-y-5 reveal reveal-delay-1">
            {visibleOffers.map((offer) => {
              const isCurrent = offer.status === "current";
              const isExpired = offer.status === "expired";
              const isUpcoming = offer.status === "upcoming";
              const daysLeft = isCurrent ? daysUntil(offer.validUntilIso) : 0;
              return (
                <div
                  key={offer.id}
                  data-testid={`offer-card-${offer.id}`}
                  className={`relative glass-card rounded-3xl p-7 md:p-8 transition-all duration-300 flex flex-col ${
                    isCurrent ? "ring-2 ring-emerald-500/30 border-emerald-200 hover:shadow-xl hover:-translate-y-1" : ""
                  } ${isExpired ? "opacity-60" : ""}`}
                >
                  {/* Status-Badge */}
                  {isCurrent && (
                    <div className="absolute -top-3 -right-3 px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full shadow-lg shadow-emerald-500/30">
                      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Aktuell
                      </span>
                    </div>
                  )}
                  {isExpired && (
                    <div className="absolute -top-3 -right-3 px-4 py-1.5 bg-stone-500 rounded-full shadow-md">
                      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Abgelaufen
                      </span>
                    </div>
                  )}
                  {isUpcoming && (
                    <div className="absolute -top-3 -right-3 px-4 py-1.5 bg-amber-500 rounded-full shadow-md">
                      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Bald verfügbar
                      </span>
                    </div>
                  )}

                  <div className="flex items-start gap-5">
                    <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ${
                      isCurrent ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30" : "bg-stone-100"
                    }`}>
                      <FileText className={`w-6 h-6 ${isCurrent ? "text-white" : "text-slate-400"}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${
                          isCurrent
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : "bg-stone-100 text-slate-500"
                        }`}>
                          <Calendar className="w-3 h-3" />
                          {offer.month}
                        </span>
                        {isCurrent && daysLeft > 0 && daysLeft <= 7 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 animate-pulse">
                            Nur noch {daysLeft} Tag{daysLeft === 1 ? "" : "e"}
                          </span>
                        )}
                      </div>

                      <h3 className={`font-bold font-[Outfit] text-xl mb-1 ${isCurrent ? "text-slate-900" : "text-slate-600"}`}>
                        {offer.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed my-4">
                        {offer.description}
                      </p>

                      {isExpired ? (
                        <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 text-slate-400 text-sm font-semibold cursor-not-allowed">
                          <Clock className="w-4 h-4" /> Dieses Angebot ist abgelaufen
                        </div>
                      ) : (
                        <a
                          href={offer.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`offer-download-${offer.id}`}
                          className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all group ${
                            isCurrent
                              ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40"
                              : "bg-white text-slate-700 border border-stone-200 hover:bg-stone-50"
                          }`}
                        >
                          <Download className="w-4 h-4" />
                          PDF herunterladen
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Quick Benefits */}
            <div className="glass-card rounded-3xl p-7 bg-gradient-to-br from-emerald-50/50 to-white border-emerald-100 mt-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Kein Startkapital",
                  "4 Verkäufe = dein TM7",
                  "Komplette Einarbeitung",
                  "Flexibel neben Job & Familie"
                ].map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 font-medium text-sm leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
              <a
                href="#contact"
                data-testid="einsteiger-cta"
                onClick={(e) => { e.preventDefault(); const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition group"
              >
                Jetzt unverbindlich anfragen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EinsteigerSection;
